import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_BRIEF_ERROR_CODES } from "@lib/shared_constants";

/**
 * Wire types shared by every brief subject (campaign, canvas). The per-subject
 * modules (`campaignBrief/`, `canvasBrief/`) own only their endpoint and its
 * id-bearing request props; every response shape is this one.
 */

/** Metadata for a persisted brief. The file bytes stay server-side. */
export interface BriefRecord {
  file_name: string;
  content_type: string;
  /** Unix seconds. */
  updated_at: number | null;
}

/**
 * The brief as a presigned attached-file descriptor for Operator chat requests
 * (the generic `attached_files` contract). The url is time-limited
 * (Brief::ATTACHED_FILE_URL_TTL_SECONDS); expires_at (epoch seconds) lets the
 * consumer refetch on an interval derived from it, so the descriptor is kept
 * fresh rather than re-minted at chat-send time.
 */
export interface BriefOperatorAttachedFile {
  url: string;
  file_name: string;
  mime_type: string;
  context: string;
  /** Epoch seconds at which `url` expires. */
  expires_at: number;
  /**
   * Epoch seconds of the file's last (re)upload -- content identity for the
   * frontend's attached-file change detection. Absent/null until the backend
   * that mints it is deployed.
   */
  file_updated_at?: number | null;
}

export interface GetBriefResult {
  brief: BriefRecord | null;
  /** null when no brief is persisted (or presigning failed server-side). */
  operator_attached_file?: BriefOperatorAttachedFile | null;
}

/**
 * Returned so the shared GET cache (which the upload UI writes with this
 * response) keeps a fresh descriptor right after an upload.
 */
export type PutBriefResult = GetBriefResult;

/** The coded errors a brief PUT returns (BriefControllerBase#upsert). */
export type PutBriefResultErrors = FetchUserEncodedError<
  SHARED_CONSTANTS_BRIEF_ERROR_CODES[
    | "MISSING_BRIEF_FILE"
    | "INVALID_BRIEF_FILE"
    | "EMPTY_BRIEF_FILE"
    | "PROTECTED_BRIEF_FILE"]
>;

export const isPutBriefResultError = (error: FetchRequestError): error is PutBriefResultErrors => {
  return error instanceof FetchUserEncodedError;
};

export interface DeleteBriefResult {
  brief: null;
}
